import { useState } from 'react'
import ModalComponent from '..'
import { formatCurrency, getTimeAgo } from '../../../helpers'
import { useAppSelector } from '../../../hooks'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useGetHarmonizedOrdersQuery,
  useLazyGetPOSOrderForResumeQuery,
  useUpdateOrderMutation,
} from '../../../redux-toolkits/orders/orders.slice'
import {
  OrderQueryProps,
  SingleHarmonizedOrder,
  SingleOrder,
} from '../../../redux-toolkits/orders/orders.type'
import { SingleCart } from '../../../redux-toolkits/cart/cart.type'
import { FulfilmentCentre } from '../../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { Button } from '../../button'
import { SearchInput } from '../../inputs/searchInput'
import { TableComponent } from '../../table'
import { ConfirmationModal } from './confirmationModal'

export const POSViewOpenOrdersModal = ({
  modalOpen,
  handleModalClose,
  setModalOpen,
  onSucess,
  fulfilmentCentre,
}: {
  modalOpen: boolean
  handleModalClose: () => void
  setModalOpen: (open: boolean) => void
  onSucess: (data: SingleOrder[], cart: SingleCart) => void
  fulfilmentCentre: FulfilmentCentre
}) => {
  const [query, setQuery] = useState<OrderQueryProps>({
    page: 1,
    limit: 20,
    status: 'OPEN',
    channel: 'GOOD_HOUSE_PHYSICAL',
    paymentStatus: undefined,
    search: undefined,
  })
  const [selectedOrder, setSelectedOrder] = useState<SingleHarmonizedOrder>()

  const { user } = useAppSelector((state) => state.auth)

  const modalControls = useModalControl()

  const { data: payload, isFetching } = useGetHarmonizedOrdersQuery(
    {
      ...query,
      fulfilmentCentre: fulfilmentCentre._id,
      adminId: user?._id,
      filter: 'inception_to_date',
      isCustomDateRange: false,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const [getOrder, { isFetching: fetchingOrder }] =
    useLazyGetPOSOrderForResumeQuery()

  const [updateOrder, { isLoading: updatingOrder }] = useUpdateOrderMutation()

  return (
    <>
      <ModalComponent
        open={modalOpen}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        title={`Your Open Orders at ${fulfilmentCentre.name}`}
        position="right"
      >
        <div className="flex flex-col gap-2">
          <SearchInput
            searchFunction={(searchText) => {
              setQuery({
                ...query,
                page: 1,
                search: searchText || undefined,
              })
            }}
            className="!max-w-full"
          />

          <TableComponent
            name={''}
            showName={false}
            headers={[]}
            rows={(payload?.data?.orders ?? []).map((order) => {
              return {
                id: order.harmonizedReference,
                content: [
                  <div
                    key={`${order.harmonizedReference}-details`}
                    className="flex flex-col border rounded-lg"
                  >
                    {[
                      {
                        title: 'Table:',
                        value: order.tableNumber,
                      },
                      {
                        title: 'Harmonized Reference:',
                        value: `#${order.harmonizedReference}`,
                      },
                      {
                        title: 'Order Time:',
                        value: getTimeAgo(order.createdAt),
                      },
                      {
                        title: 'Order Amount:',
                        value: formatCurrency(order.orderAmount),
                      },
                      {
                        title: 'Total Amount:',
                        value: formatCurrency(order.totalAmount),
                      },
                    ].map((i) => {
                      return (
                        <p
                          className="flex items-center gap-2 justify-between border-b px-4 py-3"
                          key={`${i.title}`}
                        >
                          <span className="text-black">{i.title}</span>
                          <span>{i.value}</span>
                        </p>
                      )
                    })}

                    <div className="flex gap-4 items-center justify-center px-4 py-4">
                      <Button
                        type="button"
                        label="Cancel Order"
                        onClick={() => {
                          setSelectedOrder(order)
                          modalControls.handleModalOpen('confirmCancelModal')
                        }}
                        className="!px-0 !py-0 !border-0 !hidden"
                        icon="bin"
                        variant="plain-danger"
                        loading={false}
                        disabled={true}
                      />

                      <Button
                        type="button"
                        label="Resume Order"
                        onClick={() => {
                          setSelectedOrder(order)
                          modalControls.handleModalOpen('confirmResumeModal')
                        }}
                        className="!px-0 !py-0"
                        icon="order-icon"
                        variant="plain"
                      />
                    </div>
                  </div>,
                ],
              }
            })}
            isEmpty={[undefined, 0].includes(payload?.data?.orders?.length)}
            loading={isFetching}
            currentPage={Number(query.page)}
            totalDataCount={payload?.data?.total ?? 0}
            pageLimit={Number(query.limit)}
            onPageChange={(page) => {
              setQuery({
                ...query,
                page: page,
              })
            }}
          />
        </div>
      </ModalComponent>

      {selectedOrder ? (
        <>
          <ConfirmationModal
            open={
              modalControls.modalOpen &&
              modalControls.modal === 'confirmCancelModal'
            }
            setOpen={modalControls.setModalOpen}
            message={'Are you sure you want to cancel this order?'}
            title={'Cancel this order?'}
            onCloseCallback={() => {
              modalControls.handleModalClose()
            }}
            onConfirm={async () => {
              const resp = await updateOrder({
                id: selectedOrder.harmonizedReference,
                body: {
                  status: 'CANCELLED',
                },
              })

              if (resp.data?.success) {
                modalControls.handleModalClose()
              }
            }}
            loading={updatingOrder}
            confirmText={'Cancel Order'}
          />

          <ConfirmationModal
            open={
              modalControls.modalOpen &&
              modalControls.modal === 'confirmResumeModal'
            }
            setOpen={modalControls.setModalOpen}
            message={
              'Are you sure you want to resume this order? Any details currently in progress will be replaced with this order’s details.'
            }
            title={'Resume this order?'}
            onCloseCallback={() => {
              modalControls.handleModalClose()
            }}
            onConfirm={async () => {
              const resp = await getOrder({
                harmonizedReference: selectedOrder.harmonizedReference,
                fulfilmentCentre: fulfilmentCentre._id,
              })

              if (resp.data?.success) {
                onSucess(resp.data.data.orders, resp.data.data.cart)
                modalControls.handleModalClose()
                handleModalClose()
              }
            }}
            loading={fetchingOrder}
            confirmText={'Resume Order'}
          />
        </>
      ) : undefined}
    </>
  )
}
